import React, { useState } from 'react';

import gql from 'graphql-tag';

import { useQuery } from '@apollo/client';

import { LaneType } from 'common-types';
import { getAdminClient } from 'lane-shared/apollo';
import { fromNow, longDateTimeZone } from 'lane-shared/helpers/formatters';

import IconButton from 'components/general/IconButton';
import { H3 } from 'components/typography';

import {
  PortalManagementUserEditType,
  PortalManagementUserLoginEditType,
} from '../types';

import styles from './PortalManagementUserLogins.scss';

const updateUserMutation = gql`
  mutation updateUserForUserLogins($update: UserInput!) {
    updateUser(user: $update) {
      _id
    }
  }
`;

const userLoginQuery = gql`
  query getUserLoginsForUserLogins($id: UUID!) {
    userAdmin(_id: $id) {
      _id
      logins {
        _id
        _created
        _updated
        type
        provider
        isPrimary
        key
        status
      }
    }
  }
`;

export default function PortalManagementUserLogins({
  user,
}: {
  user: PortalManagementUserEditType;
}) {
  const [loading, setLoading] = useState(false);

  const { data } = useQuery<{
    userAdmin: {
      _id: LaneType.UUID;
      logins: PortalManagementUserLoginEditType[];
    };
  }>(userLoginQuery, {
    client: getAdminClient(),
    fetchPolicy: 'network-only',
    skip: !user._id,
    variables: {
      id: user._id,
    },
  });

  const userLogins = data?.userAdmin?.logins || [];

  async function deleteUserLogin(_id: LaneType.UUID) {
    try {
      await window.Alert.confirm({
        title: 'Delete User Login.',
        message:
          'Are you sure you want to delete this user login? This cannot be undone.',
        confirmText: 'Yes, delete',
      });
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // user cancelled
      return;
    }

    try {
      setLoading(true);

      await getAdminClient().mutate({
        mutation: updateUserMutation,
        refetchQueries: ['getUserLoginsForUserLogins'],
        variables: {
          update: {
            _id: user._id,
            logins: [
              {
                _pull: true,
                _id,
              },
            ],
          },
        },
      });
    } catch (err) {
      window.Alert.alert({
        title: 'Failed',
        error: err,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.PortalManagementUserLogins}>
      <H3>User Logins</H3>
      <table className={styles.userLogins}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Type</th>
            <th>Provider</th>
            <th>Is Primary</th>
            <th>Key</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {userLogins.map(login => (
            <tr key={login._id}>
              <td>{login._id}</td>
              <td>{longDateTimeZone(login._created)}</td>
              <td>{fromNow(login._updated)}</td>
              <td>{login.type}</td>
              <td>{login.provider}</td>
              <td>{login.isPrimary}</td>
              <td>{login.key}</td>
              <td>{login.status}</td>
              <td>
                <IconButton
                  loading={loading}
                  icon="trash"
                  onClick={() => deleteUserLogin(login._id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
